import { pdfToImages } from "~/fc/Helpers/PdfHelper";
import { loadScript } from "~/fc/Helpers/DOMHelper";
import { SORTABLE_JS_CDN, PDF_JS_CDN } from "~/config/external-packages";
import { THUMB_ROTATION_OPTIONS } from '~/config/thumb.config'
import { uuidv4 } from "~/fc/Helpers/FileHelpers";
import MergeOptionHandler from "../AdvacedSettings/MergeOptionHandler";
import Job from "../Converter/job";
import { cloneObject } from "./FormatHelpers";
import { ROTATION_TEXTS } from "../constants";


/**
 * Extracts the numeric range and rotation direction from a given range string.
 *
 * @param {string} rangeString - The input string containing a numeric range and an optional rotation direction.
 * @returns {{range: string, rotation: string}} An object containing the numeric range and the rotation direction.
 * - `range` (string): The numeric range extracted from the input string (e.g., '1', '1-2', '1-2odd', '1-2even').
 * - `rotation` (string): The rotation direction (e.g., 'south', 'east', 'west') if present, otherwise an empty string.
 */
export const getRangeAndRotationFromRangeString = (rangeString) => {
    // Extract rotation from range if present
    let rotation = ''
    const directions = ['south', 'east', 'west'];
    for (const dir of directions) {
        if (rangeString.endsWith(dir)) {
            rotation = dir
        }
    }

    // Remove rotation from the string to get the numeric range part
    const range = rotation ? rangeString.slice(0, -rotation.length) : rangeString;
    
    return {
        range,
        rotation
    }
}

/**
 * Generates thumb preview elements for files.
 * @param {*} files 
 * @returns 
 */
export const extractThumbnailFromFiles = async (files) => {
    if (!files.every(file => file.file)) return;
    const pdfFiles = files.filter(file => file.ext === 'pdf');
    const imageFiles = files.filter(file => file.ext !== 'pdf');
    const errors = []
    const currentFilesLength = $nuxt.$store.state.thumb?.thumbs?.length || 0;
    
    const thumbsFromPdfFiles = await Promise.allSettled(
        pdfFiles.map(async (file, fileIndex) => {
            try {
                const images = await pdfToImages(file) || []
                const disableSortBetweenFiles = $nuxt.$store.state.info?.customVariables?.disableSortBetweenFiles || false
                const existingThumbOfFile = $nuxt.$store.state.thumb?.thumbs.find(item => item.fileId === file.id)
                const fileIndexOnStore = $nuxt.$store.state.list.findIndex(item => item.id == file.id)
                
                const partitions = $nuxt.$store.state.thumb?.thumbs.length
                    ? structuredClone($nuxt.$store.state.thumb?.thumbs).map(thumb => thumb.partition)
                    : []
                const maxPartition = partitions.length ? Math.max(...partitions) : 0

                const backgroundColor = existingThumbOfFile?.backgroundColor || generateLightSoftColor(currentFilesLength + fileIndex);

                // Incase the first thumb is already loaded, remove the first image.
                if(existingThumbOfFile && existingThumbOfFile.page === 1 && index === 0) {                    
                    $nuxt.$store.dispatch('thumb/deleteThumb', { id: existingThumbOfFile.thumbId, softDelete: true })
                }                    
                
                return images.map((image, pageIndex) => ({
                    thumbId: uuidv4(),
                    preview: image,
                    page: pageIndex + 1,
                    backgroundColor: backgroundColor,
                    name: file.name,
                    ext: file.ext,
                    total: file.total,
                    fileId: file.id,
                    include: true,
                    rotation: file.rotation || 0,
                    partition: disableSortBetweenFiles ? fileIndexOnStore : (file.partition || maxPartition),
                }));
            } catch (error) {
                errors.push({
                    file: file,
                    error: error
                })
                return null; // Return null or some error indicator
            }
        })
    );

    // if (errors.length > 0) {
    //     errors.forEach(({file, error}) => {
    //         $nuxt.$store.dispatch("removeFile", file.id)
    //         (new ToastManager($nuxt).add('danger', '', `Error: ${file.name} file is broken, please recheck the file.`))            
    //     })
    // }

    const thumbsFromImageFiles = imageFiles.map(file => ({
        preview: getUrl(file),
        name: file.name,
        ext: file.ext,
        total: file.total,
        fileId: file.id
    }));

    const extractedThumbnails = [
        ...JSON.parse(JSON.stringify(thumbsFromPdfFiles.map(files => files.value).flat().filter(Boolean))),
        ...thumbsFromImageFiles
    ];

    return extractedThumbnails.map((thumb, index) => ({
        ...thumb,
        thumbIndex: (($nuxt.$store.state.thumb.thumbs?.length || 0) + 1) + index
    }));
};

/**
 * Groups an array of files by `fileId` if adjacent files share the same `fileId`.
 * Copies properties from the first child to the parent group.
 *
 * @param {Array} thumbs - Array of file objects with properties: page, backgroundColor, name, ext, total, fileId, and optional preview.
 * @returns {Array} - Array of grouped objects with properties: fileId, name, ext, total, backgroundColor, preview, and children.
 */
export const groupAdjacentFilesByFileId = (thumbs) => {
    const grouped = [];
    let currentGroup = null;
    
    for (const thumb of thumbs) {
        if (currentGroup && currentGroup.fileId === thumb.fileId && currentGroup.partition === thumb.partition) {
            // Add to current group
            currentGroup.children.push({ ...thumb });
            // If any file is included, the group should be displayed as included
            if (thumb.include) currentGroup.include = true;
        } else {
            // Start a new group (or split when partition changes)
            currentGroup = {
                fileId: thumb.fileId,
                partition: thumb.partition, // Ensure partition is considered
                name: thumb.name,
                thumbId: thumb.thumbId || uuidv4(),
                ext: thumb.ext,
                total: thumb.total,
                backgroundColor: thumb.backgroundColor,
                preview: thumb.preview || null,
                include: thumb.include,
                rotation: thumb.rotation,
                children: [thumb]
            };
            grouped.push(currentGroup);
        }
    }

    return grouped;
};


/**
 * Flattens a grouped array of files back to its original format.
 *
 * @param {Array} groupedFiles - Array of grouped file objects, each containing a `children` array.
 * @returns {Array} - Original flat array of file objects.
 */
export const flattenGroupedFiles = (groupedFiles) => {
    return groupedFiles.map(file => (file.children || [])).flat()
}

/**
 * File rotation logic base on the direction and current rotate value.
 * @param {*} page 
 * @param {*} isClockWise 
 * @returns 
 */
export const rotateFile = (page, isClockWise) => {
    const rotations = THUMB_ROTATION_OPTIONS
    const index = rotations.indexOf(page.rotation) === -1 ? 0 : rotations.indexOf(page.rotation)
    const rotation = isClockWise
        ? index === 3
            ? rotations[0] : rotations[index + 1]
        : index === 0
            ? rotations[3] : rotations[index - 1]
    return {
        ...page,
        rotation
    }
}

/**
 * Generates Data URL for given File
 * @param {*} file 
 * @returns 
 */
const getUrl = (file) => {
    if (file.file === 'url') { return file.src }
    return URL.createObjectURL(file ? file.file : new Blob())
}

/**
 * Selects a color from a preselected color list
 * @returns {string} A hex color code.
 */
const generateLightSoftColor = (index) => {
    const colors = ['#F5C8D5', '#F5E3B1', '#ADE6ED', '#EECAF6', '#BCDAFC', '#A9EBC7', '#CDC9FB', '#C7CAD1'];
    return colors[index % colors.length];
}

/**
 * Loads up sortable js library via CDN and initiates the UI
 * @param {*} callback 
 */
export const loadSortable = (callback) => {
    if (window.Sortable) callback()
    else loadScript(SORTABLE_JS_CDN, "sortable",callback())
}

/**
 * Loads up PdfJs library via CDN and initiates the UI
 * @param {*} callback 
 */
export const loadPdfJS = (callback) => {
    if (window.pdfjsLib) callback
    else loadScript(PDF_JS_CDN, "pdfjs", callback)
}

const generateImportTasks = (list) => {
    const importTasks = {}
    const uploadTasks = {}

    list.forEach((importTask, i) => {
        if(importTask.importTaskId || importTask.import) return 
        if (importTask.file === 'url') {
            if (importTask.gtoken) {
                importTasks['import-' + i] = {
                    operation: 'import/google-drive',
                    fileId: importTask.url,
                    gtoken: importTask.gtoken,
                    filename: importTask.name,
                    filesize: importTask.total
                }
            } else {
                if (importTask.name) {
                    importTasks['import-' + i] = {
                        operation: 'import/url',
                        url: importTask.url,
                        filename: importTask.name
                    }
                } else {
                    importTasks['import-' + i] = {
                        operation: 'import/url',
                        url: importTask.url
                    }
                }
            }
        } else {
            importTasks['import-' + i] = { operation: 'import/upload' }
            uploadTasks['import-' + i] = importTask
        }
    })

    return {
        importTasks,
        uploadTasks
    }
}

const createImportTasksJob = async (list) => {
    const { importTasks, uploadTasks } = generateImportTasks(list);
    if (Object.keys(importTasks).length === 0) {
        return {}
    }
    const job = new Job(importTasks)
    return await job.processBulkFiles({ uploads: uploadTasks, jobId: uuidv4(), returnJob: true })
}

export const getPartitionsCountFromExtractPagesRangeString = (thumbs, files, rangeString) => {
    thumbs = cloneObject(thumbs).filter(t => t.include);

    // First parse the range string while keeping track of original format
    const parts = rangeString.split(',');
    const parsedParts = parts.map(part => {
        if (part.includes('-')) {
            let [start, end] = part.split('-').map(Number);
            const isReverse = start > end;
            return { 
                type: 'range', 
                start, 
                end, 
                original: part,
                isReverse 
            };
        } else {
            return { type: 'single', value: Number(part), original: part };
        }
    });

    // Group thumbs by fileId
    const thumbsByFile = {};
    const thumbIndexToPage = {};
    thumbs.forEach(thumb => {
        if (!thumbsByFile[thumb.fileId]) {
            thumbsByFile[thumb.fileId] = [];
        }

        if (thumb?.children && Array.isArray(thumb.children)) {
            for (const child of thumb.children) {
                thumbsByFile[thumb.fileId].push(child.thumbIndex);
                // Remember the original page number by thumbIndex
                thumbIndexToPage[child.thumbIndex] = String(child.page)
            }
        } else {
            thumbsByFile[thumb.fileId].push(thumb.thumbIndex);
            // Remember the original page number by thumbIndex
            thumbIndexToPage[thumb.thumbIndex] = String(thumb.page)
        }
    });

    let partitions = 0
    for (const file of files) {
        const fileThumbs = thumbsByFile[file.id] || [];
        const fileParts = [];
        
        for (const part of parsedParts) {
            if (part.type === 'single' && fileThumbs.includes(part.value)) {
                // (e.g. '1,2,3')
                fileParts.push(thumbIndexToPage[part.original]);
            } else if (part.type === 'range') {
                const relevantPages = [];
                const start = !part.isReverse ? part.start : Math.min(part.start, part.end);
                const end = !part.isReverse ? part.end : Math.max(part.start, part.end);
                
                // Get all thumb indices in the range
                for (let i = start; i <= end; i++) {
                    if (fileThumbs.includes(i)) {
                        relevantPages.push(i);
                    }
                }

                if (relevantPages.length > 0) {
                    // If reverse range, sort in descending order
                    if (part.isReverse) {
                        relevantPages.sort((a, b) => b - a);
                    }

                    // If all pages in the original range are present, keep the original range
                    if (relevantPages.length === Math.abs(part.end - part.start) + 1) {
                        // (e.g. '1-2') and pdf actually has 2 or more than 2 pages
                        fileParts.push(part.original);
                    } else {
                        // If user provides range contains multiple pdfs (e.g. '1-5' which includes 2 pdfs)
                        // then break the range into individual numbers or smaller ranges
                        const ranges = [];
                        let currentRange = [relevantPages[0]];
                        
                        for (let i = 1; i < relevantPages.length; i++) {
                            const diff = relevantPages[i] - relevantPages[i-1];
                            const direction = part.isReverse ? -1 : 1;
                            
                            if (diff === direction) {
                                currentRange.push(relevantPages[i]);
                            } else {
                                ranges.push(
                                    currentRange.length > 1 
                                        ? `${currentRange[0]}-${currentRange[currentRange.length-1]}`
                                        : currentRange[0].toString()
                                );
                                currentRange = [relevantPages[i]];
                            }
                        }
                        
                        // Add the last range
                        ranges.push(
                            currentRange.length > 1 
                                ? `${currentRange[0]}-${currentRange[currentRange.length-1]}`
                                : currentRange[0].toString()
                        );
                        
                        fileParts.push(...ranges);
                    }
                }
            }
        }

        partitions += [...new Set(fileParts)].length
    }

    return partitions
}

const generateExtractPagesRangeString = (thumbs, files, rangeString) => {
    thumbs = cloneObject(thumbs).filter(t => t.include);

    // First parse the range string while keeping track of original format
    const parts = rangeString.split(',');
    const parsedParts = parts.map(part => {
        if (part.includes('-')) {
            let [start, end] = part.split('-').map(Number);
            const isReverse = start > end;
            return { 
                type: 'range', 
                start, 
                end, 
                original: part,
                isReverse 
            };
        } else {
            return { type: 'single', value: Number(part), original: part };
        }
    });

    // Group thumbs by fileId
    const thumbsByFile = {};
    const thumbIndexToPage = {};
    thumbs.forEach(thumb => {
        if (!thumbsByFile[thumb.fileId]) {
            thumbsByFile[thumb.fileId] = [];
        }

        if (thumb?.children && Array.isArray(thumb.children)) {
            for (const child of thumb.children) {
                thumbsByFile[thumb.fileId].push(child.thumbIndex);
                // Remember the original page number by thumbIndex
                thumbIndexToPage[child.thumbIndex] = String(child.page)
            }
        } else {
            thumbsByFile[thumb.fileId].push(thumb.thumbIndex);
            // Remember the original page number by thumbIndex
            thumbIndexToPage[thumb.thumbIndex] = String(thumb.page)
        }
    });

    // Attach ranges to each file
    return files.map(file => {
        const fileThumbs = thumbsByFile[file.id] || [];
        const fileParts = [];
        
        for (const part of parsedParts) {
            if (part.type === 'single' && fileThumbs.includes(part.value)) {
                // (e.g. '1,2,3')
                fileParts.push(thumbIndexToPage[part.original]);
            } else if (part.type === 'range') {
                const relevantPages = [];
                const start = !part.isReverse ? part.start : Math.min(part.start, part.end);
                const end = !part.isReverse ? part.end : Math.max(part.start, part.end);
                
                // Get all thumb indices in the range
                for (let i = start; i <= end; i++) {
                    if (fileThumbs.includes(i)) {
                        relevantPages.push(i);
                    }
                }

                if (relevantPages.length > 0) {
                    // If reverse range, sort in descending order
                    if (part.isReverse) {
                        relevantPages.sort((a, b) => b - a);
                    }

                    // If all pages in the original range are present, keep the original range
                    if (relevantPages.length === Math.abs(part.end - part.start) + 1) {
                        // (e.g. '1-2') and pdf actually has 2 or more than 2 pages
                        fileParts.push(part.original);
                    } else {
                        // If user provides range contains multiple pdfs (e.g. '1-5' which includes 2 pdfs)
                        // then break the range into individual numbers or smaller ranges
                        const ranges = [];
                        let currentRange = [relevantPages[0]];
                        
                        for (let i = 1; i < relevantPages.length; i++) {
                            const diff = relevantPages[i] - relevantPages[i-1];
                            const direction = part.isReverse ? -1 : 1;
                            
                            if (diff === direction) {
                                currentRange.push(relevantPages[i]);
                            } else {
                                ranges.push(
                                    currentRange.length > 1 
                                        ? `${currentRange[0]}-${currentRange[currentRange.length-1]}`
                                        : currentRange[0].toString()
                                );
                                currentRange = [relevantPages[i]];
                            }
                        }
                        
                        // Add the last range
                        ranges.push(
                            currentRange.length > 1 
                                ? `${currentRange[0]}-${currentRange[currentRange.length-1]}`
                                : currentRange[0].toString()
                        );
                        
                        fileParts.push(...ranges);
                    }
                }
            }
        }

        return {
            ...file,
            extractPagesRanges: [...new Set(fileParts)],
            extractPagesRotation: thumbs.length && thumbs[0].rotation > 0 
                ? ROTATION_TEXTS[String(thumbs[0].rotation)] 
                : ''
        };
    });
};

// TODO: remove it
// const generateExtractPagesRangeString = (thumbs, files, rangeString) => {
//     thumbs = cloneObject(thumbs).filter(t => t.include);

//     // First parse the range string while keeping track of original format
//     const parts = rangeString.split(',');
//     const parsedParts = parts.map(part => {
//         if (part.includes('-')) {
//             const [start, end] = part.split('-').map(Number);
//             return { type: 'range', start, end, original: part };
//         } else {
//             return { type: 'single', value: Number(part), original: part };
//         }
//     });

//     // Group thumbs by fileId
//     const thumbsByFile = {};
//     const thumIndexToPage = {};
//     thumbs.forEach(thumb => {
//         if (!thumbsByFile[thumb.fileId]) {
//             thumbsByFile[thumb.fileId] = [];
//         }

//         if (thumb?.children && Array.isArray(thumb.children)) {
//             for (const child of thumb.children) {
//                 thumbsByFile[thumb.fileId].push(child.thumbIndex);
//                 // Remember the original page number by thumbIndex
//                 thumIndexToPage[child.thumbIndex] = String(child.page)
//             }
//         } else {
//             thumbsByFile[thumb.fileId].push(thumb.thumbIndex);
//             // Remember the original page number by thumbIndex
//             thumIndexToPage[thumb.thumbIndex] = String(thumb.page)
//         }
//     });
        
//     // Attach ranges to each file
//     return files.map(file => {
//         const fileThumbs = thumbsByFile[file.id] || [];
//         const fileParts = [];

//         for (const part of parsedParts) {                
//             if (part.type === 'single' && fileThumbs.includes(part.value)) {
//                 // (e.g. '1,2,3')
//                 fileParts.push(thumIndexToPage[part.original]);
//             } else if (part.type === 'range') {
//                 const relevantPages = [];
//                 for (let i = part.start; i <= part.end; i++) {
//                     if (fileThumbs.includes(i)) {
//                         relevantPages.push(i);
//                     }
//                 }
                
//                 if (relevantPages.length > 0) {
//                     // If all pages in the original range are present, keep the original range
//                     if (relevantPages.length === part.end - part.start + 1) {
//                         // (e.g. '1-2') and pdf actually has 2 or more than 2 pages
//                         fileParts.push(`${thumIndexToPage[part.start]}-${thumIndexToPage[part.end]}`);
//                     } else {
//                         // If user provides range contains multiple pdfs (e.g. '1-5' which includes 2 pdfs)
//                         // then break the range into individual numbers or smaller ranges
//                         const ranges = [];
//                         let start = relevantPages[0];
//                         let prev = start;

//                         for (let i = 1; i < relevantPages.length; i++) {
//                             if (relevantPages[i] !== prev + 1) {
//                                 ranges.push(start === prev ? thumIndexToPage[start] : `${thumIndexToPage[start]}-${thumIndexToPage[prev]}`);
//                                 start = relevantPages[i];
//                             }
//                             prev = relevantPages[i];
//                         }
//                         ranges.push(start === prev ? `${thumIndexToPage[start]}` : `${thumIndexToPage[start]}-${thumIndexToPage[prev]}`);
//                         fileParts.push(...ranges);
//                     }
//                 }
//             }
//         }

//         return {
//             ...file,
//             extractPagesRanges: [...new Set(fileParts)], // Remove duplicates
//             // rotation is same for all the pages
//             extractPagesRotation: thumbs.length && thumbs[0].rotation > 0 ? ROTATION_TEXTS[String(thumbs[0].rotation)] : ''
//         };
//     });
// }
/**
 * We have a few thumb/preview based convert
 * tools.
 */
export const prepareFilesForThumbBasedConversion = async ($next) => {
    const allThumbs = $next.$store.state.thumb.thumbs
    const info = $next.$store.state.info
    const list = $next.$store.state.list
    
    if ($next.$store.state.thumb.isExtractPagesPdf) {
        const rangeString = $next.$store.state.thumb.extractPagesRange
        const updatedList = generateExtractPagesRangeString(allThumbs, list, rangeString)
        $next.$store.commit("setList", updatedList)
    } else {
        await $next.$store.dispatch('thumb/generateRangeString')
    }
    
    if(info.slug === 'merge-pdf') new MergeOptionHandler().setMerge(true)
    
    if(info.slug === 'split-pdf') {
        // create a job containing only import tasks for all the files
        // and map the created taskId to file
        const job =  await createImportTasksJob(list)
        if (Array.isArray(job.tasks)) {
            job.tasks.forEach(task => {
                if (task.name) {
                    // during task creation, we keep the index of file like import-0 or import-1
                    const idx = task.name.split('-').length > 0 ? task.name.split('-')[1] : -1
                    if (idx !== -1) {
                        list[idx].importTaskId = task.id
                    }
                }
            })
        }
        
        const partitions = Array.from(new Set(allThumbs?.map(thumb => thumb.partition))).filter(x => x !== -1)
        const allRanges = $next.$store.state.thumb.ranges ? Object.entries($next.$store.state.thumb.ranges).map(([key, value]) => {
            let [partition, fileId] = key.split(':')
            fileId = fileId.includes('_') ? fileId.split('_')[0] : fileId
            return { partition, fileId, range: value }
        }) : null; 
        const fileList = partitions.map(partition => {
            const thumbs = structuredClone(allThumbs).filter(thumb => thumb.partition === partition)
            const ranges = structuredClone(allRanges).filter(range => range.partition == partition)
            const files = ranges.map(range => list.find(item => item.id == range.fileId))
            return {
                partition, thumbs, ranges, files,
                gDriveParentFolderId: files.length === 1 ? files[0].gDriveParentFolderId : null,
                id: uuidv4(),
                operation: 'merge',
                baseName: `PDF_${((partition+1) < 10) ? `0${partition+1}` : (partition+1)}`,
                name: `PDF_${((partition+1) < 10) ? `0${partition+1}` : (partition+1)}.pdf`
            }
        })
        $next.$store.commit("setSourceList", list)
        $next.$store.commit("setList", fileList)
        $next.$store.commit("setFileInputConcurrentLength", fileList.length)
    }
}

/**
 * Removes a specific index from a range string and returns the updated range string.
 * The function preserves the original structure of the range string as much as possible,
 * only modifying the parts that contain the index to be deleted.
 *
 * @param {string} rangeStr - The original range string (e.g., "1,2,4-8")
 * @param {string|number} indexToDelete - The index to remove from the range string
 * @returns {string} The updated range string with the specified index removed
 *
 * @example
 * // Basic usage
 * deleteIndexFromRangeString("1,2,4-8", "6");  // returns "1,2,4-5,7-8"
 * 
 * @example
 * // Deleting the first number in a range
 * deleteIndexFromRangeString("3-5", "3");      // returns "4-5"
 * 
 * @example
 * // Deleting from a two-number range
 * deleteIndexFromRangeString("3-4", "3");      // returns "4"
 * 
 * @example
 * // Deleting a single number
 * deleteIndexFromRangeString("1,2,3", "2");   // returns "1,3"
 * 
 * @example
 * // No change if index not found
 * deleteIndexFromRangeString("1-3,5-7", "4"); // returns "1-3,5-7"
 * 
 * @example
 * // Deleting the last number in a range
 * deleteIndexFromRangeString("5-10", "10");    // returns "5-9"
 */
export const deleteIndexFromRangeString = (rangeStr, indexToDelete) => {
    const index = parseInt(indexToDelete, 10);
    if (isNaN(index)) return rangeStr;

    const parts = rangeStr.split(',');
    const newParts = [];

    for (const part of parts) {
        if (part.includes('-')) {
            const [start, end] = part.split('-').map(Number);
            
            if (index >= start && index <= end) {
                // The index to delete is within this range
                if (start === end) {
                    // Single number range that matches exactly - skip it
                    continue;
                } else if (index === start) {
                    // Delete first number in range
                    if (start + 1 === end) {
                        // Special case: return just the end number if range was only two numbers
                        newParts.push(end.toString());
                    } else {
                        newParts.push(`${start + 1}-${end}`);
                    }
                } else if (index === end) {
                    // Delete last number in range
                    if (start === end - 1) {
                        // Special case: return just the start number if range was only two numbers
                        newParts.push(start.toString());
                    } else {
                        newParts.push(`${start}-${end - 1}`);
                    }
                } else {
                    // Split the range into two parts
                    newParts.push(`${start}-${index - 1}`, `${index + 1}-${end}`);
                }
            } else {
                // Keep the range as is
                newParts.push(part);
            }
        } else {
            // Handle single numbers
            const num = parseInt(part, 10);
            if (num !== index) {
                newParts.push(part);
            }
            // Else skip it (it's being deleted)
        }
    }

    return newParts.join(',');
}
